import type { PuzzleDefinition } from '@/types';
import { PathwayType } from '@/types';

// ═══════════════════════════════════════════════════════════════════════════
// Chapter 2: "Intervals" — Tier 2
// Introduces P5 (diff=7), M3 (diff=4), m3 (diff=3) harmonics.
// Still Clear pathways only. 2–5 nodes, 1–4 pathways.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ch2-01: "Perfect Fifth"
 * 2 nodes, 1 pathway, P5 harmony, 1 strike
 *
 * Layout:  A(1) —— B(7)
 * Harmony: A-B P5 (diff=7 required)
 *
 * Solution: Strike A → B(r1)+1=8. A=1, B=8. diff = |1-8|=7. P5 ✓
 * Verification: (8-1)%12=7. P5 satisfied ✓
 */
export const ch2_01: PuzzleDefinition = {
  id: 'ch2-01',
  version: 1,
  tier: 2,
  metadata: {
    title: 'Perfect Fifth',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.3, y: 0.5, frequency: 1, locked: false },
      { id: 'B', x: 0.7, y: 0.5, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch2-02: "Major Third"
 * 2 nodes, 1 pathway, M3 harmony, 1 strike
 *
 * Layout:  A(3) —— B(6)
 * Harmony: A-B M3 (diff=4 required)
 *
 * Solution: Strike A → B(r1)+1=7. A=3, B=7. diff = |3-7|=4. M3 ✓
 * Verification: (7-3)%12=4. M3 satisfied ✓
 */
export const ch2_02: PuzzleDefinition = {
  id: 'ch2-02',
  version: 1,
  tier: 2,
  metadata: {
    title: 'Major Third',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.3, y: 0.5, frequency: 3, locked: false },
      { id: 'B', x: 0.7, y: 0.5, frequency: 6, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'M3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch2-03: "Two Intervals"
 * 3 nodes linear, 2 pathways (P5 + M3), 1 strike
 *
 * Layout:  A(1) —— B(7) —— C(10)
 * Harmony: A-B P5 (diff=7), B-C M3 (diff=4)
 *
 * Solution: Strike A → B(r1)+1=8, C(r2)+2=12.
 *   A=1, B=8: (8-1)=7. P5 ✓
 *   B=8, C=12: (12-8)=4. M3 ✓
 */
export const ch2_03: PuzzleDefinition = {
  id: 'ch2-03',
  version: 1,
  tier: 2,
  metadata: {
    title: 'Two Intervals',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.5, frequency: 1, locked: false },
      { id: 'B', x: 0.5, y: 0.5, frequency: 7, locked: false },
      { id: 'C', x: 0.8, y: 0.5, frequency: 10, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch2-04: "Harmonic Triangle"
 * 3 nodes triangle, 3 pathways (P5 + M3 + m3), 1 strike
 *
 * Layout:      A(1)
 *             / \
 *         B(7) — C(4)
 * Harmony: A-B P5 (diff=7), A-C M3 (diff=4), B-C m3 (diff=3)
 *
 * Solution: Strike A → B(r1)+1=8, C(r1)+1=5.
 *   A=1, B=8: (8-1)=7. P5 ✓
 *   A=1, C=5: (5-1)=4. M3 ✓
 *   B=8, C=5: (8-5)=3. m3 ✓
 */
export const ch2_04: PuzzleDefinition = {
  id: 'ch2-04',
  version: 1,
  tier: 2,
  metadata: {
    title: 'Harmonic Triangle',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.15, frequency: 1, locked: false },
      { id: 'B', x: 0.15, y: 0.85, frequency: 7, locked: false },
      { id: 'C', x: 0.85, y: 0.85, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'm3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch2-05: "The Bridge"
 * 4 nodes linear, 3 pathways (P5 + M3 + P5), 1 strike — uses rings 1-3
 *
 * Layout:  A(1) —— B(7) —— C(10) —— D(2)
 * Harmony: A-B P5, B-C M3, C-D P5
 *
 * Solution: Strike A → B(r1)+1=8, C(r2)+2=12, D(r3)+3=5.
 *   A=1, B=8: (8-1)=7. P5 ✓
 *   B=8, C=12: (12-8)=4. M3 ✓
 *   C=12, D=5: (12-5)=7. P5 ✓
 */
export const ch2_05: PuzzleDefinition = {
  id: 'ch2-05',
  version: 1,
  tier: 2,
  metadata: {
    title: 'The Bridge',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.5, frequency: 1, locked: false },
      { id: 'B', x: 0.37, y: 0.5, frequency: 7, locked: false },
      { id: 'C', x: 0.63, y: 0.5, frequency: 10, locked: false },
      { id: 'D', x: 0.9, y: 0.5, frequency: 2, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch2-06: "The Diamond"
 * 4 nodes diamond, 4 pathways (P5 + M3 + M3 + P5), 1 strike with stacking
 *
 * Layout:      A(1)
 *             / \
 *          B(7)  C(4)
 *             \ /
 *             D(8)
 * Harmony: A-B P5, A-C M3, B-D M3, C-D P5
 *
 * Graph: A--B, A--C, B--D, C--D. D is at ring 2 from A via two paths.
 * Stacking: D gets delta from both A→B→D (+2) and A→C→D (+2) = +4 total.
 *
 * Solution: Strike A → B(r1)+1=8, C(r1)+1=5, D(r2)+2+2=12.
 *   A=1, B=8: (8-1)=7. P5 ✓
 *   A=1, C=5: (5-1)=4. M3 ✓
 *   B=8, D=12: (12-8)=4. M3 ✓
 *   C=5, D=12: (12-5)=7. P5 ✓
 */
export const ch2_06: PuzzleDefinition = {
  id: 'ch2-06',
  version: 1,
  tier: 2,
  metadata: {
    title: 'The Diamond',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.15, frequency: 1, locked: false },
      { id: 'B', x: 0.15, y: 0.5, frequency: 7, locked: false },
      { id: 'C', x: 0.85, y: 0.5, frequency: 4, locked: false },
      { id: 'D', x: 0.5, y: 0.85, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'B', target: 'D', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p4', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch2-07: "Two Moves"
 * 3 nodes linear, 2 pathways (P5 + P5), 2 strikes
 *
 * Layout:  A(10) —— B(7) —— C(2)
 * Harmony: A-B P5, B-C P5
 *
 * Designed by reverse from solved state A=1, B=8, C=3:
 *   A-B: (8-1)=7 P5 ✓, B-C: (8-3)=5, (3-8+12)=7 P5 ✓
 *   Reverse strike B: A-1=0→12, C-1=2. Intermediate: A=12, B=8, C=2.
 *   Reverse strike A: B-1=7, C-2=12→12. Nope, C was at 2 not 12.
 *   Actually let me re-derive. Reverse strike A on (12,8,2):
 *     B(r1)-1=7, C(r2)-2=0→12. Start: A=12, B=7, C=12.
 *
 * Hmm, let me use the verified solution from the prompt design:
 *   Start: A=10, B=7, C=2
 *   Strike C: B(r1)+1=8, A(r2)+2=12. State: A=12, B=8, C=2.
 *   Strike B: A(r1)+1=1(12+1 wraps), C(r1)+1=3. State: A=1, B=8, C=3.
 *   A-B: (8-1)=7 P5 ✓
 *   B-C: (8-3)=5, (3-8+12)=7 P5 ✓
 */
export const ch2_07: PuzzleDefinition = {
  id: 'ch2-07',
  version: 1,
  tier: 2,
  metadata: {
    title: 'Two Moves',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.5, frequency: 10, locked: false },
      { id: 'B', x: 0.5, y: 0.5, frequency: 7, locked: false },
      { id: 'C', x: 0.8, y: 0.5, frequency: 2, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['C', 'B'], optimal: true }],
};

/**
 * ch2-08: "The Triad"
 * 4 nodes (hub + 3 leaves), 3 pathways (P5 + M3 + m3), 1 strike
 *
 * Layout:      L1(7)
 *              |  P5
 *           Hub(1)
 *           /     \
 *      M3 /       \ m3
 *      L2(4)      L3(3)
 *
 * Solution: Strike Hub → L1(r1)+1=8, L2(r1)+1=5, L3(r1)+1=4.
 *   Hub=1, L1=8: (8-1)=7. P5 ✓
 *   Hub=1, L2=5: (5-1)=4. M3 ✓
 *   Hub=1, L3=4: (4-1)=3. m3 ✓
 */
export const ch2_08: PuzzleDefinition = {
  id: 'ch2-08',
  version: 1,
  tier: 2,
  metadata: {
    title: 'The Triad',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'Hub', x: 0.5, y: 0.4, frequency: 1, locked: false },
      { id: 'L1', x: 0.5, y: 0.05, frequency: 7, locked: false },
      { id: 'L2', x: 0.1, y: 0.75, frequency: 4, locked: false },
      { id: 'L3', x: 0.9, y: 0.75, frequency: 3, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'Hub', target: 'L1', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'Hub', target: 'L2', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'Hub', target: 'L3', type: PathwayType.Clear, harmonic: 'm3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['Hub'], optimal: true }],
};

/**
 * ch2-09: "Zigzag"
 * 5 nodes zigzag, 4 pathways (all P5), 1 strike — uses rings 1-3
 *
 * Layout (zigzag):
 *   A(1)     C(1)     E(5)
 *     \     / \     /
 *      B(7)   D(7)
 *
 * Pathways: A-B P5, B-C P5, C-D P5, D-E P5
 * BFS from A: ring0=A, ring1=B, ring2=C, ring3=D. E is ring4 → no effect.
 *
 * Solution: Strike A → B(r1)+1=8, C(r2)+2=3, D(r3)+3=10. E unchanged=5.
 *   A=1, B=8: (8-1)=7. P5 ✓
 *   B=8, C=3: (8-3)=5, (3-8+12)=7. P5 ✓
 *   C=3, D=10: (10-3)=7. P5 ✓
 *   D=10, E=5: (10-5)=5, (5-10+12)=7. P5 ✓
 */
export const ch2_09: PuzzleDefinition = {
  id: 'ch2-09',
  version: 1,
  tier: 2,
  metadata: {
    title: 'Zigzag',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.2, frequency: 1, locked: false },
      { id: 'B', x: 0.3, y: 0.8, frequency: 7, locked: false },
      { id: 'C', x: 0.5, y: 0.2, frequency: 1, locked: false },
      { id: 'D', x: 0.7, y: 0.8, frequency: 7, locked: false },
      { id: 'E', x: 0.9, y: 0.2, frequency: 5, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch2-10: "Grand Harmony"
 * 5 nodes linear, 4 pathways (M3 + m3 + P5 + M3), 2 strikes — chapter capstone
 *
 * Layout:  A(1) —— B(1) —— C(4) —— D(11) —— E(7)
 * Harmony: A-B M3, B-C m3, C-D P5, D-E M3
 *
 * Designed by reverse from solved state:
 *   Solved: A=1, B=5, C=8, D=3, E=7
 *     A-B: (5-1)=4 M3 ✓
 *     B-C: (8-5)=3 m3 ✓
 *     C-D: (8-3)=5, (3-8+12)=7 P5 ✓
 *     D-E: (7-3)=4 M3 ✓
 *
 *   Reverse strike E: D(r1)-1=2, C(r2)-2=6, B(r3)-3=2. A at r4 no change.
 *     Intermediate: A=1, B=2, C=6, D=2, E=7.
 *   Reverse strike A: B(r1)-1=1, C(r2)-2=4, D(r3)-3=11. E at r4 no change.
 *     Start: A=1, B=1, C=4, D=11, E=7.
 *
 * Solution:
 *   Strike A: B(r1)+1=2, C(r2)+2=6, D(r3)+3=2 (11+3=14→2). State: A=1,B=2,C=6,D=2,E=7.
 *   Strike E: D(r1)+1=3, C(r2)+2=8, B(r3)+3=5. State: A=1,B=5,C=8,D=3,E=7.
 *     A-B: (5-1)=4 M3 ✓
 *     B-C: (8-5)=3 m3 ✓
 *     C-D: (3-8+12)=7 P5 ✓
 *     D-E: (7-3)=4 M3 ✓
 */
export const ch2_10: PuzzleDefinition = {
  id: 'ch2-10',
  version: 1,
  tier: 2,
  metadata: {
    title: 'Grand Harmony',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-20',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.5, frequency: 1, locked: false },
      { id: 'B', x: 0.3, y: 0.5, frequency: 1, locked: false },
      { id: 'C', x: 0.5, y: 0.5, frequency: 4, locked: false },
      { id: 'D', x: 0.7, y: 0.5, frequency: 11, locked: false },
      { id: 'E', x: 0.9, y: 0.5, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'M3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 4,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

export const chapter2Puzzles: PuzzleDefinition[] = [
  ch2_01, ch2_02, ch2_03, ch2_04, ch2_05,
  ch2_06, ch2_07, ch2_08, ch2_09, ch2_10,
];
